// NavModeContext — app-wide navigation layout preference, shared between the
// Layout shell and the Settings toggle. "sidebar" = the classic left sidebar;
// "workspace" = the Epic-style full-width activity bar + contextual navigator.
// Persisted to localStorage (per-browser). Default "sidebar" so nothing changes
// for anyone until they choose. `engaged` is true once the user has set a
// preference at least once — used to show the quick-flip pill only to people
// who actually use the toggle.

import { createContext, useContext, useState, type ReactNode } from "react";

export type NavMode = "sidebar" | "workspace";

const STORAGE_KEY = "rb_nav_mode";

interface NavModeContextValue {
  navMode: NavMode;
  setNavMode: (m: NavMode) => void;
  engaged: boolean;
}

const NavModeContext = createContext<NavModeContextValue | null>(null);

function readStored(): NavMode | null {
  if (typeof window === "undefined") return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "workspace" || v === "sidebar" ? v : null;
}

export function NavModeProvider({ children }: { children: ReactNode }) {
  const [navMode, setNavModeState] = useState<NavMode>(() => readStored() ?? "sidebar");
  const [engaged, setEngaged] = useState<boolean>(() => readStored() !== null);

  const setNavMode = (m: NavMode) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, m);
    } catch {
      /* ignore storage failures (private mode, etc.) */
    }
    setNavModeState(m);
    setEngaged(true);
  };

  return (
    <NavModeContext.Provider value={{ navMode, setNavMode, engaged }}>
      {children}
    </NavModeContext.Provider>
  );
}

export function useNavMode(): NavModeContextValue {
  const ctx = useContext(NavModeContext);
  if (!ctx) throw new Error("useNavMode must be used within a NavModeProvider");
  return ctx;
}
